import { AES_GCM, Pbkdf2HmacSha256 } from 'asmcrypto.js';
import { dbGet, dbPut } from '@p4b/utils-db';
import { chainError, sharedBufferToArrayBuffer } from './utils';
import { Reader, Writer } from '@zip.js/zip.js'

export class DecryptionError extends Error {
    constructor(message: string) {
        super(message);
        Object.defineProperty(this, 'name', { value: 'DecryptionError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, DecryptionError);
    }
}

//----------------------------------------------------------------------------
// Stream Reader & Writer for IndexedDB

export class IDBWriter extends Writer<void> {
    private buf: Uint8Array;
    private ptrChunk: number;
    private ptrOffset: number;
    private store: string;
    private dbChunkSize: number;
    private streamClosed: boolean = false;

    public constructor(store: string, dbChunkSize: number) {
        super();
        this.store = store;
        this.dbChunkSize = dbChunkSize;
        this.buf = new Uint8Array(dbChunkSize);
        this.ptrChunk = 0;
        this.ptrOffset = 0;
    }

    public getPtr(): number {
        return this.ptrChunk * this.dbChunkSize + this.ptrOffset;
    }

    public async writeUint8Array(data: Uint8Array): Promise<void> {
        try {
            if (this.ptrOffset + data.length < this.buf.length) {
                this.buf.set(data, this.ptrOffset);
                this.ptrOffset += data.length;
            } else if (this.ptrOffset + data.length < 2 * this.buf.length) {
                const split = this.buf.length - this.ptrOffset;
                this.buf.set(data.subarray(0, split), this.ptrOffset);
                await dbPut(this.store, this.buf.buffer, this.ptrChunk++);
                this.buf.set(data.subarray(split), 0);
                this.ptrOffset = data.length - split;
            } else {
                let offset = this.buf.length - this.ptrOffset;
                this.buf.set(data.subarray(0, offset), this.ptrOffset);
                await dbPut(this.store, this.buf.buffer, this.ptrChunk++);
                while (offset + this.buf.length < data.length) {
                    this.buf.set(data.subarray(offset, offset + this.buf.length), 0);
                    await dbPut(this.store, this.buf.buffer, this.ptrChunk++);
                    offset += this.buf.length;
                }
                this.buf.set(data.subarray(offset), 0);
                this.ptrOffset = data.length - offset;
            }
        } catch (err) {
            console.error('IDBWriter writeUint8Array error:', err);
            throw err;
        }
    }

    public async getData(): Promise<void> {
        // No-op: zip.js expects this method but we don't need it
    }

    public async close(): Promise<void> {
        if (!this.streamClosed && this.ptrOffset > 0) {
            await dbPut(this.store, this.buf.buffer, this.ptrChunk++);
            this.ptrOffset = 0;
        }
        this.streamClosed = true;
    }
}

export class IDBReader extends Reader<void> {
    private cacheId?: number;
    private cacheData?: ArrayBuffer;
    private store: string;
    private dbChunkSize: number

    public constructor(store: string, dbChunkSize: number) {
        super();
        this.store = store;
        this.dbChunkSize = dbChunkSize;
    }

    private async readBuf(chunkId: number): Promise<ArrayBuffer> {
        if (this.cacheData && chunkId === this.cacheId) {
            return this.cacheData;
        }
        const data = await dbGet<ArrayBuffer>(this.store, chunkId);
        if (data == undefined) {
            throw 'Image store key does not exist: ' + chunkId;
        }
        this.cacheData = data;
        this.cacheId = chunkId;
        return data;
    }

    public async readUint8ArrayPromise(start: number, end: number): Promise<Uint8Array<ArrayBuffer>> {
        const startChunk = Math.floor(start / this.dbChunkSize);
        const startOffset = start - (startChunk * this.dbChunkSize);
        const endChunk = Math.floor(end / this.dbChunkSize);
        const endOffset = end - (endChunk * this.dbChunkSize);
        const length = end - start;

        if (startChunk === endChunk) {
            const buf1 = (await this.readBuf(startChunk)).slice(startOffset, endOffset);
            return new Uint8Array(buf1);
        } else if (startChunk + 1 === endChunk) {
            const data = new Uint8Array(length);
            const buf1 = await this.readBuf(startChunk);
            data.set(new Uint8Array(buf1, startOffset));
            const buf2 = await this.readBuf(endChunk);
            data.set(new Uint8Array(buf2, 0, endOffset), this.dbChunkSize - startOffset);
            return data;
        } else {
            const data = new Uint8Array(length);
            const buf1 = await this.readBuf(startChunk);
            data.set(new Uint8Array(buf1, startOffset));
            let offset = this.dbChunkSize - startOffset;
            for (let chunk = startChunk + 1; chunk < endChunk; ++chunk) {
                const buf2 = await this.readBuf(chunk);
                data.set(new Uint8Array(buf2), offset);
                offset += this.dbChunkSize;
            }
            const buf3 = await this.readBuf(endChunk);
            data.set(new Uint8Array(buf3, 0, endOffset), offset);
            return data;
        }
    }

    public close(): void {
        this.cacheId = undefined;
        this.cacheData = undefined;
    }
}


//----------------------------------------------------------------------------
// Read Encrypted Stream from IndexedDB

// WebCrypto

async function webcryptoDeriveKey(subtle: SubtleCrypto, pin: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
    const textEncoder = new TextEncoder();
    const material = await subtle.importKey('raw', textEncoder.encode(pin), 'PBKDF2', false, ['deriveKey']);
    const key = await subtle.deriveKey(
        {
            name: 'PBKDF2',
            hash: 'SHA-256',
            salt: salt.buffer,
            iterations,
        },
        material,
        {
            name: 'AES-GCM',
            length: 256
        },
        true,
        ['decrypt']
    );
    return key;
}

async function webcryptoImportKey(subtle: SubtleCrypto, key: ArrayBuffer): Promise<CryptoKey> {
    return subtle.importKey('raw', key, {name: 'AES-GCM'}, false, ['decrypt']) as Promise<CryptoKey>;
}

async function webcryptoDecryptChunk(
    subtle: SubtleCrypto,
    aesKey: CryptoKey,
    aesIv: Uint8Array<ArrayBuffer>,
    ciphertext: Uint8Array<ArrayBuffer>
): Promise<ArrayBuffer> {
    const plaintext = await subtle.decrypt({ 'name': 'AES-GCM', 'iv': aesIv.buffer}, aesKey, ciphertext.buffer);
    return plaintext;
}

// AsmCrypto

function asmcryptoDeriveKey(pin: string): ArrayBuffer {
    const textEncoder = new TextEncoder();
    const result = Pbkdf2HmacSha256(textEncoder.encode(pin), textEncoder.encode('Practique'), 1009, 32);
    return sharedBufferToArrayBuffer(result.buffer);
}

function asmcryptoDecryptChunk(
    aesKey: ArrayBuffer,
    aesIv: Uint8Array,
    ciphertext: Uint8Array
): ArrayBuffer {
    const result = AES_GCM.decrypt(ciphertext, new Uint8Array(aesKey), aesIv, undefined, 16).buffer;
    return sharedBufferToArrayBuffer(result);
}

declare global {
    interface Crypto {
        webkitSubtle: typeof crypto.subtle;
    }
}

class Decryptor {
    private webcryptoAesKey?: CryptoKey;
    private asmcryptoAesKey?: ArrayBuffer;
    private subtle?: SubtleCrypto;
    private pin: string;

    constructor(pin: string) {
        this.subtle = crypto.subtle || crypto.webkitSubtle;
        this.pin = pin;
    }

    async decryptChunk(buf: ArrayBuffer): Promise<ArrayBuffer> {
        const aesIv = new Uint8Array(buf.slice(0, 12))
            , ciphertext = new Uint8Array(buf.slice(12, buf.byteLength))
            ;

        if (this.subtle && this.webcryptoAesKey) {
            return await webcryptoDecryptChunk(this.subtle, this.webcryptoAesKey, aesIv, ciphertext);
        } else if (this.asmcryptoAesKey) {
            return asmcryptoDecryptChunk(this.asmcryptoAesKey, aesIv, ciphertext);
        } else if (this.subtle) {
            try {
                console.debug('TRY NEW WEBCRYPTO DERIVE KEY');
                const webcryptoAesKey = await webcryptoDeriveKey(this.subtle, this.pin, aesIv, 1000000);
                try {
                    console.debug('TRY WEBCRYPTO DECRYPT');
                    const data = await webcryptoDecryptChunk(this.subtle, webcryptoAesKey, aesIv, ciphertext);
                    this.webcryptoAesKey = webcryptoAesKey;
                    return data
                } catch (err) {
                    console.warn(String(err));
                    console.debug("TRY OLD WEBCRYPTO KEY IMPORT");
                    const webcryptoAesKey = await webcryptoDeriveKey(this.subtle, this.pin, new TextEncoder().encode('Practique'), 1009);
                    console.debug('TRY WEBCRYPTO DECRYPT');
                    const data = await webcryptoDecryptChunk(this.subtle, webcryptoAesKey, aesIv, ciphertext);
                    this.webcryptoAesKey = webcryptoAesKey;
                    return data;
                }
            } catch (e1) {
                console.warn(String(e1));
                console.debug("TRY ASMCRYPTO KEY IMPORT", e1)
                const asmcryptoAesKey = asmcryptoDeriveKey(this.pin);
                try {
                    console.debug('TRY WEBCRYPTO DECRYPT');
                    const webcryptoAesKey = await webcryptoImportKey(this.subtle, asmcryptoAesKey);
                    const data = await webcryptoDecryptChunk(this.subtle, webcryptoAesKey, aesIv, ciphertext);
                    this.webcryptoAesKey = webcryptoAesKey;
                    return data;
                } catch (e2) {
                    console.warn(String(e2));
                    console.debug("ASMCRYPTO  DECRYPT");
                    const data = asmcryptoDecryptChunk(asmcryptoAesKey, aesIv, ciphertext);
                    this.asmcryptoAesKey = asmcryptoAesKey;
                    return data;
                }
            }
        } else {
            console.debug('ASMCRYPTO');
            const asmcryptoAesKey = asmcryptoDeriveKey(this.pin);
            const data = asmcryptoDecryptChunk(asmcryptoAesKey, aesIv, ciphertext);
            this.asmcryptoAesKey = asmcryptoAesKey;
            return data;
        }
    }
}

export type ExamId = [string, number];;

export class IndexedDBReader extends Reader<void> {
    private decryptor: Decryptor;
    private cacheId: number | null;
    private cacheValue: ArrayBuffer | null;
    private store: string;
    private examId: ExamId;
    private dbChunkSize: number;
    public readonly size: number; // zip.js needs to read this field
    private err: unknown = '';

    get lastError() {
        return this.err;
    }

    public constructor(
        store: string,
        key: string,
        examId: ExamId,
        dbChunkSize: number,
        size: number
    ) {
        super();
        this.decryptor = new Decryptor(key);
        this.store = store;
        this.examId = examId;
        this.dbChunkSize = dbChunkSize;
        this.size = size;
        this.cacheId = null;
        this.cacheValue = null;
        console.log('CONSTRUCT: IndexedDBReader', store, key, examId, dbChunkSize, size);
    }

    private async getChunk(chunkId: number): Promise<ArrayBuffer> {
        if (chunkId == this.cacheId && this.cacheValue != null) {
            return this.cacheValue;
        } else {
            const ciphertext = await dbGet<ArrayBuffer>(this.store, [...this.examId, chunkId]);
            if (!ciphertext) {
                throw new Error('IndexedDBReader: getChunk ' + chunkId + ' null');
            }
            try {
                const plaintext = await this.decryptor.decryptChunk(ciphertext);
                this.cacheId = chunkId;
                this.cacheValue = plaintext;
                //console.log('GOT CHUNK');
                return plaintext;
            } catch(err) {
                console.error('IndexedDBReader: Decryption error chunk ' + chunkId, err);
                throw chainError(DecryptionError, err, `Error decrypting chunk ${chunkId}`);
            }
        }
    }

    public async readUint8Array(
        start: number,
        length: number,
    ): Promise<Uint8Array<ArrayBuffer>> {
        try {
            //console.log(start, length, this.dbChunkSize);
            const startChunk = Math.floor(start / this.dbChunkSize);
            const startOffset = start - (startChunk * this.dbChunkSize);
            const end = start + length;
            const endChunk = Math.floor(end / this.dbChunkSize);
            const endOffset = end - (endChunk * this.dbChunkSize);

            //console.log('IndexedDBReader readUint8Array: start chunk:', startChunk, 'offset:', startOffset, ' ->  end chunk:', endChunk, 'offset:', endOffset);
            if (startChunk === endChunk) {
                // optiminse single chunk
                const buf = await this.getChunk(startChunk);
                return new Uint8Array(buf.slice(startOffset, endOffset));
            } else if (startChunk + 1 === endChunk) {
                // optimise spanning two chunks
                const buf = new Uint8Array(length);
                const buf1 = await this.getChunk(startChunk);
                buf.set(new Uint8Array(buf1).subarray(startOffset));
                const buf2 = await this.getChunk(endChunk);
                buf.set(new Uint8Array(buf2).subarray(0, endOffset), this.dbChunkSize - startOffset);
                return buf;
            } else {
                const buf = new Uint8Array(length);
                const buf1 = await this.getChunk(startChunk);
                buf.set(new Uint8Array(buf1).subarray(startOffset));
                let offset = this.dbChunkSize - startOffset;
                for (let chunk = startChunk + 1; chunk < endChunk; ++chunk) {
                    const buf2 = await this.getChunk(chunk);
                    buf.set(new Uint8Array(buf2), offset);
                    offset += this.dbChunkSize;
                }
                const buf3 = await this.getChunk(endChunk);
                buf.set(new Uint8Array(buf3).subarray(0, endOffset), offset);
                return buf;
            }
        } catch (err) {
            console.error('IndexedDBReader readUint8Array error:', err);
            this.err = err;
            throw err;
        }
    }

    public close(): void {
        this.cacheId = null;
        this.cacheValue = null;
    }
}
